/**
 * The queue row's shared styling decisions, and the two lines of prose derived
 * from an item's money.
 *
 * Split out of `ReviewQueueTable` when the row and its action cell became their
 * own components: all three need these, and none of them owns them.
 */
import { formatMoney } from './reviewQueueModel';
import type { ReviewQueueItem } from './reviewTypes';

/** The secondary line under a name. `--nb-muted` (5.89:1), NOT `--nb-subtle`
 *  (4.13:1 at this size) — this line carries the booking reference, which is a
 *  LINK, plus the venue and the timestamps. */
export const SUBTLE = { fontSize: 11, color: 'var(--nb-muted)' } as const;

/**
 * De-emphasis for a resolved row.
 *
 * A COLOUR, not `opacity`. The row used to carry `opacity: .62`, which was
 * harmless while the mock's resolved row was inert text — but the row now holds a
 * real button, and opacity multiplies through to every descendant: it took the
 * button's white-on-green to 2.45:1 and the resolved-at stamp to 2.21:1. You
 * cannot dim a container and exempt the control inside it, so the dimming moves
 * to the text that should recede and the control keeps full contrast.
 */
export const MUTED_INK = 'var(--nb-muted)' as const;

export function inkFor(muted: boolean): string | undefined {
  return muted ? MUTED_INK : undefined;
}

/** What was actually taken, against what we recorded. */
export function paidLine(item: ReviewQueueItem): string {
  if (item.amountPaidCents === null) {
    return item.paymentIntentId ? 'paid amount unknown' : 'no payment intent';
  }
  if (item.amountPaidCents === item.totalCents) return 'paid in full';
  return `${formatMoney(item.amountPaidCents, item.currency)} paid`;
}

/** Warn-tinted ONLY where the money disagrees with the order — a queue where
 *  every row is orange says nothing. A muted row keeps the muted ink: the row is
 *  settled, so the mismatch is history rather than a call to action. */
export function paidTone(item: ReviewQueueItem, muted: boolean): string {
  if (muted) return MUTED_INK;
  const settled = item.amountPaidCents !== null && item.amountPaidCents === item.totalCents;
  return settled ? MUTED_INK : 'var(--nb-warn)';
}

/**
 * Two lines, then an ellipsis. Free-form prose was running to five lines and
 * destroying the row rhythm; the full string stays on the cell's `title`, and
 * the detail page still shows it whole.
 *
 * Exported so a test can pin it: happy-dom discards vendor-prefixed
 * declarations, so `-webkit-line-clamp` never reaches the rendered style
 * attribute under test even though every target browser honours it.
 */
export const REASON_CLAMP_STYLE = {
  display: '-webkit-box',
  WebkitBoxOrient: 'vertical' as const,
  WebkitLineClamp: 2,
  overflow: 'hidden',
  fontSize: 12.5,
  color: 'var(--nb-ink-2)',
} as const;
